<script>
    import { mapActions, mapMutations, mapState } from 'vuex';
    import {
        Checkbox,
        Form,
        FormItem,
        Input,
        InputNumber,
        MessageBox,
        Option,
        Select,
        Switch,
        Tooltip
    } from 'element-ui';
    import Vue from "vue";
    import VueButton from '~/components/button/components/button';
    import Panel from '~/components/panel/components/panel';
    import Slideout from '~/components/slideout/components/slideout';

    Vue.use(Input);
    Vue.use(InputNumber);
    Vue.use(Form);
    Vue.use(FormItem);
    Vue.use(Switch);
    Vue.use(Tooltip);
    Vue.use(Checkbox);
    Vue.use(Select);
    Vue.use(Option);

    export default {
        name: 'org-security-questions-form',

        components: {
            Slideout,
            Panel,
            VueButton
        },

        data() {
            return {
                editing: false
            }
        },

        computed: {
            ...mapState('passwordSettings', [
                'loading',
                'orgSecurityQuestionsForm',
                'dialogVisible'
            ]),

            isLoading: {
                get() {
                    return this.loading.orgSecurityQuestions;
                },

                set(value) {
                    this.updateLoading({
                        orgSecurityQuestions: value
                    });
                }
            },
        },

        created() {
            this.getOrgSecurityQuestionsForm();
            this.orgSecurityQuestionsForm.customOrgSecurityQuestions.forEach(question => {
                question.editing = false
            });
        },

        methods: {
            ...mapMutations('passwordSettings', [
                'updateDialogVisible',
                'updateLoading'
            ]),

            ...mapActions('passwordSettings', [
                'getOrgSecurityQuestionsForm',
                'postOrgSecurityQuestionsForm'
            ]),

            onOpen() {
                this.$emit('open');
            },

            onClose() {
                this.updateDialogVisible({
                    orgSecurityQuestions: false
                });
                this.$emit('close');
            },

            addSecurityQuestion() {
                this.orgSecurityQuestionsForm.customOrgSecurityQuestions.push({
                    id: null,
                    question: null,
                    is_default: '0',
                    editing: true
                });

                this.editing = true;
            },

            editSecurityQuestion(securityQuestion) {
                securityQuestion.editing = true;
                this.editing = true;
            },

            saveSecurityQuestion(securityQuestion, index) {
                securityQuestion.editing = false;
                this.editing = false;

                if (!securityQuestion.question) { // Remove question if no text was provided
                    this.orgSecurityQuestionsForm.customOrgSecurityQuestions.splice(index, 1);
                }

            },

            saveSecurityQuestionsForm() {
                this.postOrgSecurityQuestionsForm();
                this.onClose();
            },

            removeSecurityQuestion(userCount, index) {
                let confirmMsg = "Are you sure you want to delete this custom question?";

                if (userCount) {
                    confirmMsg += ` ${userCount} user(s) will be required to answer a new question on their next login.`;
                }

                MessageBox.confirm(confirmMsg, "Warning", {
                    confirmButtonText: 'Yes',
                    cancelButtonText: 'Cancel',
                    type: 'warning'
                }).then(() => {
                    this.orgSecurityQuestionsForm.customOrgSecurityQuestions.splice(index, 1);
                });
            },

            getCannotEditMessage(userCount) {
                if (userCount !== null && userCount > 0) {
                    return `This question cannot be edited because ${userCount} user(s) have answered it.`;
                }
            }
        }
    }
</script>

<template>
    <slideout title="Customize Security Questions"
              :visible.sync="dialogVisible.orgSecurityQuestions"
              direction="rtl"
              size="440px"
              custom-class="org-security-questions-panel"
              @open="onOpen"
              @close="onClose"
              :destroyOnClose="true">

        <div class="org-security-questions-panel">
            <loader :is-loading="loading.orgSecurityQuestions"></loader>
            <el-form ref="orgSecurityQuestionSettingsForm"
                     :model="orgSecurityQuestionsForm">

                <h5>Default Questions</h5>
                <div class="security-question-toggle" v-for="defaultSecurityQuestion in orgSecurityQuestionsForm.defaultOrgSecurityQuestions">
                    <el-form-item style="margin-bottom: 0; line-height: 0">
                        <el-checkbox v-model="defaultSecurityQuestion.enabled"
                                     true-label=1
                                     false-label=0
                                     style="margin-bottom: 0; line-height: 0">
                            {{ defaultSecurityQuestion.question }}
                        </el-checkbox>
                    </el-form-item>
                </div>

                <hr class="ps-form__hr">

                <h5>Custom Questions</h5>
                <div class="custom-questions" v-for="(customSecurityQuestion, index) in orgSecurityQuestionsForm.customOrgSecurityQuestions">
                    <el-form-item style="margin-bottom: 0; line-height: 0"
                                  :prop="'customOrgSecurityQuestions.' + index + '.question'"
                                  :rules="{required: true, message: 'This is required.', trigger: 'blur'}">
                        <el-input v-if="customSecurityQuestion.editing"
                                  v-model="customSecurityQuestion.question"
                                  placeholder="Type a question..."
                                  :maxlength="400">
                        </el-input>
                        <vue-button v-if="customSecurityQuestion.editing"
                                    type="primary"
                                    size="xs"
                                    :loading="loading.orgSecurityQuestions"
                                    @click="saveSecurityQuestion(customSecurityQuestion, index)">
                            <i class="fa fa-check"></i>
                            Done
                        </vue-button>

                        <el-checkbox v-if="!customSecurityQuestion.editing"
                                     v-model="customSecurityQuestion.enabled"
                                     true-label=1
                                     false-label=0>
                            {{ customSecurityQuestion.question }}
                        </el-checkbox>

                        <div class="security-question-buttons"
                             v-if="!customSecurityQuestion.editing">
                            <el-tooltip :disabled="customSecurityQuestion.userCount <= 0"
                                        :content="getCannotEditMessage(customSecurityQuestion.userCount)">
                                <span> <!-- Button is wrapped in span tags in order to display tooltip when it's disabled -->
                                    <vue-button type="link"
                                                icon="fa fa-pencil"
                                                :disabled="customSecurityQuestion.userCount > 0"
                                                @click="editSecurityQuestion(customSecurityQuestion)"
                                                :readonly="customSecurityQuestion.userCount > 0">
                                    </vue-button>
                                </span>
                            </el-tooltip>

                            <vue-button type="link"
                                        class="m-r-7 p-2"
                                        icon="fa fa-trash"
                                        @click="removeSecurityQuestion(customSecurityQuestion.userCount, index)">
                            </vue-button>
                        </div>

                    </el-form-item>
                </div>

                <vue-button v-if="!editing"
                            type="primary"
                            size="xs"
                            :loading="loading.orgSecurityQuestions"
                            @click="addSecurityQuestion()">
                    <i class="fa fa-plus"></i>
                    Add Question
                </vue-button>

                <hr class="ps-form__hr">

                <div class="save-security-questions-btn">
                    <vue-button type="primary"
                                size="sm"

                                :disabled="editing"
                                :loading="loading.orgSecurityQuestions"
                                @click="saveSecurityQuestionsForm()">
                        Save Security Questions
                    </vue-button>
                </div>
            </el-form>
        </div>
    </slideout>
</template>